import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getReport, updateReport } from "@/lib/api/admin";
import { Spinner, SpinnerCenter } from "@/components/Spinner";
import { Textarea } from "@/components/Textarea";
import { Select } from "@/components/Select";
import type { SelectOption } from "@/components/Select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime } from "@/lib/utils/date";
import { REPORT_STATUSES } from "@/lib/constants";

const REPORT_STATUS_OPTIONS: SelectOption[] = REPORT_STATUSES.map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1),
}));

export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAdminAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    data: report,
    isLoading,
    isError: reportError,
  } = useQuery({
    queryKey: ["report", id],
    queryFn: async () => getReport(user!, id!),
    enabled: !!user && !!id,
  });

  const [status, setStatus] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () =>
      updateReport(user!, id!, {
        status: status ?? undefined,
        admin_notes: notes ?? undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["report", id] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
      setStatus(null);
      setNotes(null);
      toast.success("Report updated.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const currentStatus = status ?? report?.status ?? "pending";
  const currentNotes = notes ?? report?.admin_notes ?? "";

  if (isLoading) return <SpinnerCenter />;

  if (reportError)
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
        <AlertCircle className="size-8 text-muted-foreground/50" />
        <p className="font-medium text-muted-foreground">Failed to load report</p>
      </div>
    );

  if (!report) return null;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => void navigate(-1)}>
          <ArrowLeft className="size-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Report #{report.id.slice(-8)}</h1>
          <p className="text-sm text-muted-foreground">
            Submitted {formatDateTime(report.created_at)}
          </p>
        </div>
      </div>

      {/* Report info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Report Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-muted-foreground text-xs uppercase">Target Type</p>
              <p className="capitalize font-medium">{report.target_type}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs uppercase">Target ID</p>
              <p className="font-mono text-xs">{report.target_id}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs uppercase">Reason</p>
              <p>{report.reason}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs uppercase">Status</p>
              <StatusBadge status={report.status} />
            </div>
          </div>
          {report.description && (
            <div>
              <p className="text-muted-foreground text-xs uppercase">Description</p>
              <p className="mt-1">{report.description}</p>
            </div>
          )}
          <div>
            <p className="text-muted-foreground text-xs uppercase">Reporter</p>
            <p>
              {report.reporter_first_name} {report.reporter_last_name}
              <span className="ml-2 text-muted-foreground font-mono text-xs">
                ({report.reporter_uid})
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Admin review */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Admin Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select
            id="rd-status"
            label="Status"
            value={currentStatus}
            onValueChange={setStatus}
            options={REPORT_STATUS_OPTIONS}
          />

          <Textarea
            id="rd-notes"
            label="Admin notes"
            placeholder="Internal notes about this report..."
            value={currentNotes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            maxLength={1000}
          />

          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || (status === null && notes === null)}
          >
            {mutation.isPending ? <Spinner className="size-4 border-primary-foreground" /> : "Save"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
